/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import CustomerModel from 'cio/models/customer';
import AuthService from 'cio/services/auth';
import SubscriptionCenterService from 'cio/services/subscription-center';
import { computed } from '@ember/object';
import {
  TopicWithPreference,
  SubscriptionPreferences as SubscriptionPreferencesType,
} from 'cio/utils/subscription-center';

interface SubscriptionPreferencesArgs {
  customer: CustomerModel;
}

export default class SubscriptionPreferences extends Component<SubscriptionPreferencesArgs> {
  @service auth!: AuthService;
  @service subscriptionCenter!: SubscriptionCenterService;

  @tracked showManagePrefsModal = false;
  @tracked computedCustomerPreferences: SubscriptionPreferencesType = { topics: {} };

  constructor(owner: unknown, args: SubscriptionPreferencesArgs) {
    super(owner, args);

    this.subscriptionCenter.initializeTask.perform();
  }

  @computed('subscriptionCenter.allTopics')
  get subscriptionCenterHasTopics() {
    return (this.subscriptionCenter.allTopics ?? []).length > 0;
  }

  get subscribedTopics() {
    let { isGloballyUnsubscribed, subscriptionPreferences } = this.args.customer;
    let userTopics = subscriptionPreferences.topics ?? {};

    return this.subscriptionCenter.topics.filter((topic) => {
      if (isGloballyUnsubscribed) {
        return false;
      }

      // customer is subscribed to an optIn topic
      if (topic.isOptIn) {
        return Object.keys(userTopics).includes(topic.identifier) && userTopics[topic.identifier] === true;
      }

      // testing for optOut topics.
      return !Object.keys(userTopics).includes(topic.identifier) || userTopics[topic.identifier] === true;
    });
  }

  get subscribedTopicNames() {
    return this.subscribedTopics.map((topic) => topic.name);
  }

  get subscribedTopicsWithPrefs() {
    return this.subscribedTopics.map(
      (topic): TopicWithPreference => ({
        id: topic.identifier,
        name: topic.name,
        preference: true,
        description: topic.description,
      }),
    );
  }

  get joinedSubTopicNames() {
    return this.subscribedTopicNames.join(', ');
  }

  get unsubscribedTopics() {
    let { isGloballyUnsubscribed, subscriptionPreferences } = this.args.customer;
    let userTopics = subscriptionPreferences.topics ?? {};

    return this.subscriptionCenter.topics.filter((topic) => {
      if (isGloballyUnsubscribed) {
        return true;
      }

      // if optInTopics are not present in cio_subscription_preferences.topics, user is (not-subscribed).
      // if optInTopics are present, value should be true
      if (topic.isOptIn) {
        return !Object.keys(userTopics).includes(topic.identifier) || userTopics[topic.identifier] !== true;
      }

      // if user has value for optOut topic, they are likely unsubscribed
      return Object.keys(userTopics).includes(topic.identifier) && userTopics[topic.identifier] !== true;
    });
  }

  get unsubscribedTopicsWithPrefs() {
    return this.unsubscribedTopics.map(
      (topic): TopicWithPreference => ({
        id: topic.identifier,
        name: topic.name,
        preference: false,
        description: topic.description,
      }),
    );
  }

  get unsubscribedTopicNames() {
    return this.unsubscribedTopics.map((topic) => topic.name);
  }

  get joinedUnsubTopicNames() {
    return this.unsubscribedTopicNames.join(', ');
  }

  get topicsWithPrefs() {
    let topics = this.subscribedTopicsWithPrefs.concat(this.unsubscribedTopicsWithPrefs);
    /*
      Computed customer preferences are needed for situations where
      1. The customer is globally subscribed but do not have any preferences set yet.
        In this situation, the topic preference is overridden by the optIn/optOut rule set for the Topic.
        reference: fn unsubscribedTopics && fn subscribedTopics
      2. A new topic is created and the customer is yet to set preference for it
    */
    this.computedCustomerPreferences = topics.reduce(
      (acc: SubscriptionPreferencesType, topic) => {
        acc.topics[topic.id] = topic.preference;
        return acc;
      },
      { topics: {} },
    );
    return topics;
  }
}
